/**
 * Care32's master lab catalogue and the price lists built on it. Every lab
 * item a clinic orders is one catalogue item: priced by a registered lab, or
 * by the clinic itself for a lab it added by hand.
 */

export type PricingMethod = "flat" | "base_plus_tooth";

export type CatalogItem = {
  id: string;
  categoryId: string;
  name: string;
  description: string;
  pricingMethod: PricingMethod;
};

export type CatalogCategory = {
  id: string;
  name: string;
  /** Digit in clinic case IDs: Denture = 1 → 2610-101 */
  caseCode: number;
  items: CatalogItem[];
};

/** The four price fields; null = not set. */
export type Prices = {
  price: number | null;
  basePrice: number | null;
  toothPrice: number | null;
  includedTeeth: number | null;
};

export const emptyPrices = (): Prices => ({
  price: null,
  basePrice: null,
  toothPrice: null,
  includedTeeth: null,
});

/**
 * One row of a lab's price list (GET client/labs/items), the same shape for
 * a registered lab (labItemId) and a hand-added lab (localItemId).
 */
export type PriceListRow = {
  id: string;
  labItemId?: string;
  localItemId?: string;
  catalogItemId: string | null;
  /** The lab's own name, else the catalogue name */
  name: string;
  catalogName: string;
  categoryId: string | null;
  categoryName: string;
  caseCode: number | null;
  pricingMethod: PricingMethod;
  /** What this clinic pays (a registered lab's clinic price wins) */
  prices: Prices;
  hasPrice: boolean;
  status?: "active" | "inactive";
};

export const MISSING_ITEM_HINT = "Can't find it? Ask Care32 support to add it to the catalogue.";

const lkr = (n: number | null | undefined) => `LKR ${(Number(n) || 0).toLocaleString("en-LK")}`;

export function hasPrice(method: PricingMethod, p: Partial<Prices>) {
  return method === "base_plus_tooth" ? p.basePrice != null && p.toothPrice != null : p.price != null;
}

/** "LKR 10,000" or "Base LKR 15,000 + LKR 1,500 / tooth (4 incl.)"; "Not set" when missing. */
export function priceSummary(method: PricingMethod, p: Partial<Prices>) {
  if (method === "base_plus_tooth") {
    if (p.basePrice == null || p.toothPrice == null) return "Not set";
    const incl = Number(p.includedTeeth) || 0;
    return `Base ${lkr(p.basePrice)} + ${lkr(p.toothPrice)} / tooth${incl ? ` (${incl} incl.)` : ""}`;
  }
  return p.price == null ? "Not set" : lkr(p.price);
}

/** Same rule as the server: flat, or base + per tooth beyond the teeth in the base. */
export function unitTotal(method: PricingMethod, p: Partial<Prices>, teeth: number | null) {
  if (method === "base_plus_tooth") {
    const extra = Math.max(0, (Number(teeth) || 0) - (Number(p.includedTeeth) || 0));
    return (Number(p.basePrice) || 0) + (Number(p.toothPrice) || 0) * extra;
  }
  return Number(p.price) || 0;
}

/** "Base 15,000 (4 teeth incl.) + 2 × 1,500"; "" for flat items. */
export function breakdownLabel(method: PricingMethod | undefined, p: Partial<Prices>, teeth: number | null) {
  if (method !== "base_plus_tooth") return "";
  const n = (v: number | null | undefined) => (Number(v) || 0).toLocaleString("en-LK");
  const included = Number(p.includedTeeth) || 0;
  const extra = Math.max(0, (Number(teeth) || 0) - included);
  return `Base ${n(p.basePrice)}${included ? ` (${included} teeth incl.)` : ""} + ${extra} ${included ? "" : "teeth "}× ${n(p.toothPrice)}`;
}

/** FDI tooth numbers picked on the chart (arch tokens like "upper" don't count). */
export function chartTeethCount(toothNos: string[]) {
  return (toothNos || []).filter((t) => /^\d{2}$/.test(String(t).trim())).length;
}

export function findCatalogItem(catalog: CatalogCategory[], id: string) {
  for (const category of catalog) {
    const item = category.items.find((i) => i.id === id);
    if (item) return { item, category };
  }
  return null;
}

/** Price list rows grouped by category, categories in case-code order. */
export function groupRows(rows: PriceListRow[]) {
  const groups = new Map<string, { name: string; caseCode: number | null; rows: PriceListRow[] }>();
  for (const row of rows) {
    const key = row.categoryId || row.categoryName;
    if (!groups.has(key)) groups.set(key, { name: row.categoryName, caseCode: row.caseCode, rows: [] });
    groups.get(key)!.rows.push(row);
  }
  return Array.from(groups.values()).sort(
    (a, b) => (a.caseCode ?? 99) - (b.caseCode ?? 99) || a.name.localeCompare(b.name)
  );
}
